import { isNil, pickBy } from "lodash";

export * from "./vite";
export const backendFolder = "api/";
export const modelsFolderSegment = "models/";
export const globalActionsFolderSegment = "actions/";
export const schemaMetadataFileName = "schema.gadget.ts";

export const PermissionsLocation = "accessControl/permissions";

export const modelsFolderPath = `${backendFolder}${modelsFolderSegment}`;
export const globalActionsFolderPath = `${backendFolder}${globalActionsFolderSegment}`;

export const isExcludedNamespaceSegment = (segment: string) => {
  return segment[0] === "(" && segment[segment.length - 1] === ")";
};

export type PathPreferenceSpec = {
  backendFolder: string;
  frontendFolder: string;
  shopifyExtensionsFolder: string;
  frontendRoutesFolder: string;
  modelsFolderPath: string;
  globalActionsFolderPath: string;
  routesPath: string;
  bootPluginsPath: string;
  computedViewsPath: string;
  backendRouteFileRegex: RegExp;
  getReactRouterTypeImportStatement: (routeFilePath: string) => string;
  getActionFilePath: (modelId: string, actionId: string, typescript?: boolean) => string;
  getActionFolderPath: (props: { namespacedModelApiId: string; schemaFilePath?: string }) => string;
  getModelFilterFolder: (props: { filterPath: string; schemaFilePath?: string }) => string;
  getBackendRouteFilePath(routeFilename: string): string;
  getBootPluginFilePath(pluginFilename: string): string;
  getModelSchemaFilePath(modelId: string, namespace: string[]): string;
};

export const version1PathPreferences: PathPreferenceSpec = {
  backendFolder,
  frontendFolder: "web/",
  shopifyExtensionsFolder: "extensions/",
  frontendRoutesFolder: "web/routes/",
  modelsFolderPath,
  globalActionsFolderPath,
  routesPath: "api/routes/",
  bootPluginsPath: "api/boot/",
  computedViewsPath: "api/views/",
  backendRouteFileRegex: /^\/?api\/routes\/((\/|[^[:*]*)\/)?GET(-?([^:[*]*)(\..+)?)?\.(js|ts)$/,
  getReactRouterTypeImportStatement: (routeFilePath: string) => {
    const routeFilename = routeFilePath
      .split("/")
      .pop()
      ?.replace(/\.[^.]+$/, "");
    return `import type { Route } from "./+types/${routeFilename}";`;
  },
  getActionFilePath: (modelId: string, actionId: string, typescript?: boolean) => {
    return `${modelsFolderPath}${modelId}/actions/${actionId}.${typescript ? "ts" : "js"}`;
  },
  getActionFolderPath: (props: { namespacedModelApiId: string; schemaFilePath?: string }) => {
    const { namespacedModelApiId, schemaFilePath } = props;
    if (schemaFilePath && schemaFilePath.endsWith(schemaMetadataFileName)) {
      return schemaFilePath.slice(0, -schemaMetadataFileName.length) + "actions/";
    }
    return `${modelsFolderPath}${namespacedModelApiId}/actions/`;
  },
  getModelFilterFolder: (props: { filterPath: string; schemaFilePath?: string }) => {
    const { filterPath, schemaFilePath } = props;
    if (schemaFilePath && schemaFilePath.endsWith(schemaMetadataFileName)) {
      return "accessControl/filters/" + schemaFilePath.slice(0, -schemaMetadataFileName.length).slice(modelsFolderPath.length);
    }
    return `accessControl/filters/${filterPath}`;
  },
  getBackendRouteFilePath(routeFilename: string) {
    return `${this.routesPath}${routeFilename}`;
  },
  getBootPluginFilePath(pluginFilename: string) {
    return `${this.bootPluginsPath}${pluginFilename}`;
  },
  getModelSchemaFilePath(modelId: string, namespace: string[]) {
    return `${modelsFolderPath}${[...namespace, modelId].join("/")}/schema.gadget.ts`;
  },
};

export const aacPathPreferences: PathPreferenceSpec = {
  backendFolder: "",
  frontendFolder: "frontend/",
  shopifyExtensionsFolder: "extensions/",
  frontendRoutesFolder: "frontend/routes/",
  modelsFolderPath: "",
  globalActionsFolderPath: "globalActions/",
  routesPath: `routes/`,
  bootPluginsPath: `boot/`,
  computedViewsPath: `views/`,
  backendRouteFileRegex: /^\/?routes\/((\/|[^[:*]*)\/)?GET(-?([^:[*]*)(\..+)?)?\.(js|ts)$/,
  getReactRouterTypeImportStatement: (routeFilePath: string) => {
    const routeFilename = routeFilePath
      .split("/")
      .pop()
      ?.replace(/\.[^.]+$/, "");
    return `import type { Route } from "./+types/${routeFilename}";`;
  },
  getActionFilePath: (modelId: string, actionId: string, typescript?: boolean) => {
    return `${modelId}/actions/${actionId}.${typescript ? "ts" : "js"}`;
  },
  getActionFolderPath: (props: { namespacedModelApiId: string }) => {
    const { namespacedModelApiId } = props;
    return `${namespacedModelApiId}/actions/`;
  },
  getModelFilterFolder: (props: { filterPath: string }) => {
    const { filterPath } = props;
    return `accessControl/filters/${filterPath}`;
  },
  getBackendRouteFilePath(routeFilename: string) {
    return `${this.routesPath}${routeFilename}`;
  },
  getBootPluginFilePath(pluginFilename: string) {
    return `${this.bootPluginsPath}${pluginFilename}`;
  },
  getModelSchemaFilePath(modelId: string, _namespace: string[]) {
    return `${modelsFolderPath}${modelId}/schema.gadget.ts`;
  },
};

export const getPathPreferences = (flags: { fileBasedActions: boolean }) => {
  return flags.fileBasedActions ? version1PathPreferences : aacPathPreferences;
};

export type ModelTransitionState =
  | Record<
      string,
      {
        from?: Record<string, string> | string;
        to: Record<string, string> | string;
      }
    >
  | undefined;

export const supportedViteConfigPaths = [
  "vite.config.js",
  "vite.config.ts",
  "vite.config.mjs",
  "vite.config.mts",
  "frontend/vite.config.js", // Legacy frontend beta apps
  "web/vite.config.js", // Legacy frontend beta apps that are upgraded to v1.0
] as const;

export const maybeFindViteConfigFilename = async (
  finder: (viteConfigPath: (typeof supportedViteConfigPaths)[number]) => Promise<boolean>
) => {
  for (const viteConfigPath of supportedViteConfigPaths) {
    if (await finder(viteConfigPath)) {
      return viteConfigPath;
    }
  }
};

export const isLegacyViteConfigLocation = (viteConfigPath: (typeof supportedViteConfigPaths)[number]) => {
  return viteConfigPath === "web/vite.config.js" || viteConfigPath === "frontend/vite.config.js";
};

export const UNKNOWN_SHOPIFY_APP_URL = "https://not-known-at-build-time.gadget.app";

export const getDevHarnessOtelCollectorUrl = (isProduction: boolean) => {
  return isProduction ? "https://traces.gadget.dev" : "http://localhost:4318";
};

export const getFrontendEnvVariables = (
  platformVariables: {
    appSlug: string;
    environmentSlug: string;
    appUrl: string;
    shopifyAppUrl?: string;
    appTemplateType: string;
    environmentId: string;
    otelCollectorUrl?: string;
    frameworkVersion?: string;
  },
  userVariables: Record<string, string | null>,
  flags?: {
    assistantEnabled?: boolean;
    hyperDxEnabled?: boolean;
  }
) => {
  const publicEnvVariables = pickBy(userVariables, (_value, key) => {
    return key.toLowerCase().startsWith("gadget_public");
  });
  return pickBy(
    {
      ...publicEnvVariables,
      GADGET_APP: platformVariables.appSlug,
      GADGET_ENV: platformVariables.environmentSlug,
      GADGET_APP_URL: platformVariables.appUrl,
      GADGET_PUBLIC_APP_SLUG: platformVariables.appSlug,
      GADGET_ENV_ID: platformVariables.environmentId,
      GADGET_PUBLIC_APP_ENV: platformVariables.environmentSlug,
      GADGET_PUBLIC_SHOPIFY_APP_URL: platformVariables.shopifyAppUrl,
      GADGET_APP_TEMPLATE_TYPE: platformVariables.appTemplateType,
      GADGET_OTEL_COLLECTOR_URL: platformVariables.otelCollectorUrl,
      GADGET_FRAMEWORK_VERSION: platformVariables.frameworkVersion,
      GADGET_FLAG_ASSISTANT_ENABLED: flags?.assistantEnabled ? "true" : undefined,
      GADGET_FLAG_HYPERDX_ENABLED: flags?.hyperDxEnabled ? "true" : undefined,
    },
    (value) => !isNil(value)
  ) as {
    GADGET_APP: string;
    GADGET_ENV: string;
    GADGET_APP_URL: string;
    GADGET_PUBLIC_APP_SLUG: string;
    GADGET_ENV_ID: string;
    GADGET_PUBLIC_APP_ENV: string;
    GADGET_PUBLIC_SHOPIFY_APP_URL?: string;
    GADGET_APP_TEMPLATE_TYPE: string;
    GADGET_OTEL_COLLECTOR_URL?: string;
    GADGET_FRAMEWORK_VERSION?: string;
    GADGET_FLAG_ASSISTANT_ENABLED?: string;
    GADGET_FLAG_HYPERDX_ENABLED?: string;
  } & Record<string, string>;
};

/**
 * Where a log message originates from -- Gadget's platform code, or a user's code like an action or route
 */
export enum LogSource {
  Platform = "platform",
  User = "user",
  Command = "command",
}

export const FrameworkPackageName = "gadget-server";

export const appBridgeCDNUrl = "https://cdn.shopify.com/shopifycloud/app-bridge.js";
export const mockAppBridgeParam = "gadgetmockappbridge";
